import React from 'react'
import { DataActionResults } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechBadge } from '@agtech/web/components/Badges/AgTechBadge';
import AgTechButtonPane from '@agtech/web/components/Buttons/AgTechButtonPane';
import AgTechGrid from '@agtech/web/components/Grids/AgTechGrid';
import { AgTechGridTextControl } from '@agtech/web/components/Grids/Controls/AgTechGridTextControl';
import { useBidderDataUpdateBidderRegistrationAction } from '@sales/data/bidders/BidderDataActions';
import { SaleContactActionButton, SaleContactGridLink } from '../../../contacts/components/buttons/SaleContactButtons';
import BidderRegisterNewContactDialog from '../../dialogs/BidderRegisterNewContactDialog';
import { useBidderDataGetContactBiddersQuery } from '@sales/data/bidders/BidderDataQueries';
import { useAgTechQueryResultProjection } from '@agtech/core/data/queries/AgTechDataQueries';
import { SaleContactBidder } from '@sales/data/bidders/BidderData';
import { getContactAddress, getContactFullName } from '@agtech/data/contacts/ContactData';
import ContactDialog from '@agtech/web/integrations/contacts/dialogs/ContactDialog';
import ContactImportModal from '@agtech/web-imports/integrations/ContactImportModal';

const SaleBidderRegistrationSelectionGrid = () => {
    const getContactBiddersQuery = useBidderDataGetContactBiddersQuery();
    const updateContactBidderRegistrationAction = useBidderDataUpdateBidderRegistrationAction();

    const gridQuery = useAgTechQueryResultProjection({
        queryRun: getContactBiddersQuery,
        projection: (index) => ({
            items: Object.values(index)
        })
    });

    return (
        <AgTechGrid<SaleContactBidder>
            header={{
                title: 'Contacts',
                content: () => <BidderRegisterNewContactDialog.Button />
            }}
            data={{
                itemQuery: gridQuery
            }}
            columns={[
                {
                    header: 'Name',
                    value: contact => (
                        <SaleContactGridLink
                            contactId={contact.id}
                            contactName={getContactFullName(contact)}
                        />
                    ),
                    classes: 'bg-slate-100 emphasized',
                },
                {
                    header: 'Bidder Number',
                    value: (contact, _, update) => (
                        <AgTechGridTextControl
                            value={contact.bidder?.bidderNumber ?? ''}
                            onUpdated={val => update(orig => ({
                                ...orig,
                                bidder: {
                                    id: orig.bidder?.id ?? 0,
                                    bidderNumber: val
                                }
                            }))}
                        />
                    ),
                    width: '130px'
                },
                {
                    header: 'Ranch Name',
                    value: contact => (
                        <AgTechGridTextControl
                            value={contact.ranchName ?? '-'}
                            readonly={true}
                        />
                    ),
                    classes: 'bg-slate-100',
                },
                {
                    header: 'Address',
                    value: contact => (
                        <AgTechGridTextControl
                            value={getContactAddress(contact)}
                            readonly={true}
                        />
                    ),
                    size: 2,
                    classes: 'bg-slate-100'
                },
                {
                    header: '',
                    value: contact => contact.bidder?.bidderNumber && contact.bidder?.id
                        ? <AgTechBadge classes='wide tall border-none color-green text-green-800' text="Registered" />
                        : <AgTechBadge classes='color-gray wide tall border-none text-primary' text="Not Registered" />,
                    width: '154px',
                    classes: 'snug',
                    contentClasses: 'wide bg-slate-100'
                }
            ]}
            actions={{
                rendering: contact => (
                    <SaleContactActionButton contact={contact} />
                )
            }}
            isRowEntityTheSame={(prev, curr) => {
                return prev.bidder?.bidderNumber === curr.bidder?.bidderNumber &&
                        prev.ranchName === curr.ranchName &&
                        prev.phoneNumber === curr.phoneNumber;
            }}
            searchConfig={{
                options: [
                    { field: 'Name', value: contact => getContactFullName(contact)},
                    { field: 'Ranch Name', value: contact => contact.ranchName},
                    { field: 'Phone', value: contact => contact.phoneNumber},
                    { field: 'Bidder Number', value: contact => contact.bidder?.bidderNumber ?? ''},
                ]
            }}
            editConfig={{
                onEntityUpdated: async props => {
                    const originalBidder = {
                        id: props.originalItem.bidder?.id!,
                        bidderNumber: props.originalItem.bidder?.bidderNumber!,
                        contact: props.originalItem
                    };

                    const updatedBidder = {
                        id: props.entity.item.bidder?.id!,
                        bidderNumber: props.entity.item.bidder?.bidderNumber!,
                        contact: props.entity.item
                    };

                    const updateResult = await props.actionExecutor.executeAction(updateContactBidderRegistrationAction, {
                        originalEntity: originalBidder,
                        submittedEntity: updatedBidder
                    });

                    return updateResult.success
                        ? DataActionResults.Success({
                              ...props.entity.item,
                              bidder: updateResult.success.data
                                  ? { id: updateResult.success.data.id, bidderNumber: updateResult.success.data.bidderNumber }
                                  : undefined
                          })
                        : DataActionResults.Failed(updateResult.errorMessage);
                },
                showStatus: false
            }}
            noRecordsConfig={{
                message: 'There are no contacts in the system right now.',
                action: {
                    prompt: 'To create or import contacts, use the buttons below',
                    button: () => (
                        <AgTechButtonPane>
                            <ContactDialog.Button classes='button-highlight' />
                            <ContactImportModal.Button classes='button-highlight' />
                        </AgTechButtonPane>
                    )
                }
            
            }}
        />
    )
};

export default SaleBidderRegistrationSelectionGrid;